import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import MapComponent from '../components/Map';
import { Marker } from 'react-map-gl/maplibre';
import { LocalPoi } from '../interface/LocalPoi';
import { getPOI } from '../hooks/GetLocalPoi';
import { IconButton } from '@mui/material';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import { getOSMPOI } from '../hooks/GetOsmPoi';
import { BoundingBox } from '../interface/BoundingBox';
import CategoryIcon from '../components/CategoryIcon';
import InfoWindow from '../components/InfoWindow';
import LayersIcon from '@mui/icons-material/Layers';
import CategoriesWindow from '../components/CategoriesWindow';
import supercluster from 'supercluster';
import { ClusterFeature, isCluster } from '../customTypes/ClusterProperties';
import { GeoJSONFeatureCollection } from '../customTypes/GeoJSONFeature';
import Search from '../components/Search';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { setPosition } from '../redux/mapSlice';
import { setMunicipality } from '../redux/municipialitySlice';
import { getMunicipality } from '../hooks/GetMunicipality';
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import { addMessage } from '../redux/infoMessageSlice';

export default function MapPage() {
  const [pois, setPois] = useState<LocalPoi[]>([]);
  const [osmPois, setOsmPois] = useState<LocalPoi[]>([]);
  const [boundingBox, setBoundingBox] = useState<BoundingBox>({
    max_latitude: 0,
    max_longitude: 0,
    min_latitude: 0,
    min_longitude: 0,
  });
  const authHeader = useAuthHeader();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();
  const [selectedPoi, setSelectedPoi] = useState<LocalPoi | null>(null);
  const [isSelectedPoiLocal, setSelectedPoiLocal] = useState<boolean>(false);
  const [layersWindowOpen, setLayersWindowOpen] = useState(false);
  const [clusters, setClusters] = useState<ClusterFeature[]>([]);
  const zoom = useSelector((state: RootState) => state.map.zoom);
  const poiReloadTrigger = useSelector(
    (state: RootState) => state.map.poiReloadTrigger
  );
  const [visibleCategoryIds, setVisibleCategoryIds] = useState<number[]>(() => {
    const savedCategories = localStorage.getItem('visibleCategories');
    return savedCategories ? JSON.parse(savedCategories) : [];
  });
  const municipality = useSelector(
    (state: RootState) => state.municipality.selectedMunicipality
  );

  useEffect(() => {
    if (!isAuthenticated) {
      //window.location.reload();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let fetchedMunicipality;
    if (!municipality && authHeader) {
      fetchedMunicipality = getMunicipality(authHeader, dispatch);
      fetchedMunicipality.then((fetchedMunicipality) => {
        if (fetchedMunicipality !== null) {
          dispatch(setMunicipality(fetchedMunicipality));
        }
      });
    }
  }, []);

  const updateBoundingBox = (box: BoundingBox) => {
    setBoundingBox(box);
  };

  const handleToggleCategory = (id: number) => {
    const currentVisibleCategoryIds = visibleCategoryIds;
    let updatedVisibleCategories: number[];
    if (currentVisibleCategoryIds.includes(id)) {
      updatedVisibleCategories = currentVisibleCategoryIds.filter(
        (categoryId) => categoryId !== id
      );
    } else {
      updatedVisibleCategories = [...currentVisibleCategoryIds, id];
    }
    localStorage.setItem(
      'visibleCategories',
      JSON.stringify(updatedVisibleCategories)
    );
    setVisibleCategoryIds(updatedVisibleCategories);
  };

  const updateSelectedPoi = (updatedPois: LocalPoi[]) => {
    if (selectedPoi && !isSelectedPoiLocal) {
      const updatedPoi = updatedPois.find((poi) => poi.id === selectedPoi.id);
      if (updatedPoi) {
        setSelectedPoi(updatedPoi);
      } else {
        setSelectedPoi(null);
        setSelectedPoiLocal(false);
      }
    }
  };

  const fetchPOIs = async () => {
    const auth = authHeader || '';
    const response = await getPOI(auth);
    if (auth != '') {
      // Only fetch Local POIs if user is logged in to avoid unnecessary errors
      if (typeof response !== 'string') {
        setPois(response);
      } else {
        dispatch(
          addMessage({ message: 'error.failedToFetch', error: response })
        );
      }
    }
  };

  useEffect(() => {
    fetchPOIs();
  }, [authHeader, poiReloadTrigger]);

  useEffect(() => {
    const fetchOSMPOIs = async () => {
      const auth = authHeader || '';
      const response = await getOSMPOI(
        auth,
        boundingBox.min_latitude,
        boundingBox.min_longitude,
        boundingBox.max_latitude,
        boundingBox.max_longitude
      );
      if (typeof response !== 'string') {
        setOsmPois(response);
        updateSelectedPoi(response);
      }
    };
    fetchOSMPOIs();
  }, [authHeader, boundingBox]);

  useEffect(() => {
    const categoryClusterIndexes: Record<number, supercluster> = {};

    const filteredPois = osmPois.filter((poi) =>
      visibleCategoryIds.includes(poi.category.id)
    );

    const poisByCategory = filteredPois.reduce(
      (acc, poi) => {
        const categoryId = poi.category.id;
        if (!acc[categoryId]) {
          acc[categoryId] = [];
        }
        acc[categoryId].push(poi);
        return acc;
      },
      {} as Record<number, LocalPoi[]>
    );

    const allClusters: ClusterFeature[] = [];
    Object.entries(poisByCategory).forEach(([categoryId, pois]) => {
      const numericCategoryId = Number(categoryId);
      const index = new supercluster({
        radius: 60,
        maxZoom: 20,
      });

      const geoJson: GeoJSONFeatureCollection = pois.map((poi) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [poi.longitude, poi.latitude],
        },
        properties: {
          poi: poi,
          categoryId: poi.category.id,
        },
      }));
      index.load(geoJson);
      categoryClusterIndexes[numericCategoryId] = index;
      const bounds: [number, number, number, number, number, number] = [
        boundingBox.min_longitude, // west
        boundingBox.min_latitude, // south
        boundingBox.max_longitude, // east
        boundingBox.max_latitude, // north
        0,
        30,
      ];
      const clustersData = index.getClusters(bounds, zoom) as ClusterFeature[];
      const clusterColorData = clustersData.map((cluster) => {
        if (isCluster(cluster.properties)) {
          return {
            ...cluster,
            properties: {
              ...cluster.properties,
              color: pois[0].category.color,
            },
          };
        } else {
          return cluster;
        }
      });
      allClusters.push(...clusterColorData);
    });
    setClusters(allClusters);
  }, [osmPois, visibleCategoryIds, boundingBox, zoom]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedPoi(null);
        setSelectedPoiLocal(false);
        setLayersWindowOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleClusterClick = (cluster: ClusterFeature) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    const newZoom = zoom + 1;
    dispatch(setPosition({ longitude, latitude, zoom: newZoom }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Navbar />
      <div style={{ flex: 1, position: 'relative' }}>
        <MapComponent onBoundingBoxChange={updateBoundingBox}>
          {pois
            .filter((poi) => visibleCategoryIds.includes(poi.category.id))
            .map((poi) => (
              <Marker
                key={poi.id}
                longitude={poi.longitude}
                latitude={poi.latitude}
                onClick={() => {
                  setSelectedPoi(poi);
                  setSelectedPoiLocal(true);
                  setLayersWindowOpen(false);
                }}
              >
                <CategoryIcon
                  category={poi.category.name}
                  color={poi.category.color}
                  icon={poi.category.icon}
                  isSelected={selectedPoi === poi}
                />
              </Marker>
            ))}
          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;

            if (isCluster(cluster.properties)) {
              return (
                <Marker
                  key={`cluster-${cluster.properties.cluster_id}`}
                  longitude={longitude}
                  latitude={latitude}
                  onClick={() => handleClusterClick(cluster)}
                >
                  <div
                    style={{
                      width: `20px`,
                      height: `20px`,
                      backgroundColor: `${cluster.properties.color}`,
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: 'white',
                      fontSize: '16px',
                      padding: '4px',
                      border: '2px solid white',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)',
                      cursor: 'pointer',
                    }}
                  >
                    {cluster.properties.point_count}
                  </div>
                </Marker>
              );
            }

            return (
              <Marker
                key={`poi-${cluster.properties.poi.id}`}
                longitude={longitude}
                latitude={latitude}
                onClick={() => {
                  if (!cluster.properties.cluster) {
                    setSelectedPoi(cluster.properties.poi);
                  }
                  setSelectedPoiLocal(false);
                  setLayersWindowOpen(false);
                }}
              >
                <CategoryIcon
                  category={cluster.properties.poi.category.name}
                  color={cluster.properties.poi.category.color}
                  icon={cluster.properties.poi.category.icon}
                  isSelected={cluster.properties.poi === selectedPoi}
                />
              </Marker>
            );
          })}
        </MapComponent>
        <Search />
        <IconButton
          id={'layer-button'}
          onClick={() => {
            setLayersWindowOpen(true);
            setSelectedPoi(null);
          }}
          sx={{
            position: 'absolute',
            top: 120,
            left: 22,
            color: '#1976d2',
            backgroundColor: '#ffffff',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.6)',
            '&:hover': {
              backgroundColor: '#f7f7f8',
            },
          }}
        >
          <LayersIcon fontSize="medium" />
        </IconButton>
        {layersWindowOpen && (
          <CategoriesWindow
            visibleCategoryIds={visibleCategoryIds}
            onToggleCategory={handleToggleCategory}
            onClose={() => setLayersWindowOpen(false)}
          />
        )}
        {selectedPoi && (
          <InfoWindow
            poi={selectedPoi}
            isLocalPoi={isSelectedPoiLocal}
            onClose={() => {
              setSelectedPoi(null);
              setSelectedPoiLocal(false);
              fetchPOIs();
            }}
          />
        )}
      </div>
    </div>
  );
}
